
import Vue, { VueConstructor } from 'vue'
import { createNamespacedHelpers } from 'vuex'

import {
  ConfirmPaymentModal,
  UpgradeSuccessfulModal,
  RemoveComputersModal,
  ConfirmDowngradeModal,
  DowngradeSuccessfulModal,
} from '@/components/modals'

import { RButton, RLoader } from '@/components'
import BundleSelection from '@/components/wizard/BundleSelection.vue'

import MQ from '@/mixins/MediaQuery'

import { BundlePreset } from '@/store/types/config'

import { WIZARD_STEP_NAME, WIZARD_STEP_STORAGE_TIER } from '@/services/drives'
import { fitsWithin } from '@/services/numbers'
import { getBillingPeriodEndStringFromSecondsTimestamp } from '@/services/date'
import { BundlePeriod } from '@/api/types/user'
import { openInNewTab } from '@/services/routing'

const { mapGetters: mapUserGetters, mapActions: mapUserActions } = createNamespacedHelpers('user')
const { mapActions: mapNotificationsActions } = createNamespacedHelpers('notification')
const { mapGetters: mapConfigGetters, mapActions: mapConfigActions } =
  createNamespacedHelpers('config')
const { mapGetters: mapWorkstationGetters } = createNamespacedHelpers('workstation')

type SelectPlanData = {
  newPlan: BundlePreset | null // I need to let the modals know about (still not picked) bundle's codename, price
  bundlePeriod: BundlePeriod | null
}

export default (Vue as VueConstructor<Vue & InstanceType<typeof MQ>>).extend({
  name: 'SelectPlanView',
  components: {
    BundleSelection,
    RLoader,
    RButton,
    ConfirmPaymentModal,
    UpgradeSuccessfulModal,
    RemoveComputersModal,
    ConfirmDowngradeModal,
    DowngradeSuccessfulModal,
  },
  mixins: [MQ],
  data(): SelectPlanData {
    return {
      newPlan: null,
      bundlePeriod: null,
    }
  },
  created() {
    this.getConfig()
  },
  computed: {
    wizardStepName(): string {
      return WIZARD_STEP_NAME
    },
    wizardStepStorageTier(): string {
      return WIZARD_STEP_STORAGE_TIER
    },

    currentlyPickedPlanCodename(): string {
      return this.selectedTierInStore?.codename || ''
    },
    ...mapUserGetters({
      selectedTierInStore: 'selectedTier',
      storageTierUpdateRequest: 'storageTierUpdateRequest',
      storagePlan: 'storagePlan',
    }),
    ...mapConfigGetters({
      selectableBundles: 'selectableBundles',
      getBundleRequestPending: 'getConfigRequestPending',
      isRegionDisabled: 'isRegionDisabled',
    }),
    ...mapWorkstationGetters(['numberOfOwnerWorkstations', 'hasRunningWorkstations']),
    variant(): string {
      return this.mq === 'sm' ? 'mobile' : 'desktop'
    },
    // no support for optional chaining in Vue 2 templates
    capitalizedPlanNameIfAny(): string {
      return this.newPlan?.name.toUpperCase() || '???'
    },
    planPriceIfAny(): string | undefined {
      // perhaps we could hide the model implementation
      // (and especially this particular field name)
      // behind some price-getting api? 🤔
      return this.newPlan && this.bundlePeriod
        ? this.newPlan[this.bundlePeriod]?.priceUsd
        : this.newPlan?.priceUsdPerMonth
      // return this.newPlan?.priceUsdPerMonth
    },
    nextBillingPeriodStart(): string | null {
      if (!this.storagePlan?.created) return null
      const { created } = this.storagePlan
      return getBillingPeriodEndStringFromSecondsTimestamp(created)
    },
  },
  methods: {
    handleSelectBundle({
      bundle,
      bundlePeriod,
    }: {
      bundle: BundlePreset
      bundlePeriod: BundlePeriod
    }): void {
      const { codename } = bundle
      const selectableCodenameList = this.selectableBundles.map(
        (bundle: BundlePreset) => bundle.codename,
      )
      const chosenGrade = selectableCodenameList.indexOf(codename)

      // ERROR HANDLING
      if (!~chosenGrade || !codename) {
        // means the chosen grade not among the selectableBundles
        this.error('An issue occured.')
        return
      }
      const periodCheck = this.bundlePeriod === bundlePeriod
      if (codename === this.currentlyPickedPlanCodename && periodCheck) {
        this.error('The bundle is already picked!')
        return
      }
      this.newPlan = bundle
      this.bundlePeriod = bundlePeriod

      // in case the user was missing storage plan, just choose it.
      if (!this.selectedTierInStore && !this.selectedTierInStore?.codename) {
        this.changePlan()
      }
      // determine whether upgrade / downgrade / downgrade and limit
      // it assumes the bundles are sorted by their "grade"
      const currentGrade = selectableCodenameList.indexOf(this.currentlyPickedPlanCodename)

      if (chosenGrade > currentGrade) {
        // upgrade logic
        this.$rModal.show('confirmPayment')
      } else {
        // downgrade logic
        const newWorkstationLimit = this.newPlan.limits.workstations
        this.numberOfOwnerWorkstations

        // it is assumes that upgrading means always "fitting within", which might not always be true
        if (fitsWithin(this.numberOfOwnerWorkstations, newWorkstationLimit)) {
          // this.$rModal.show('confirmDowngrade')
          this.$rModal.show('confirmPayment')
        } else {
          this.$rModal.show('removeComputers')
        }
      }
    },

    async changePlan(): Promise<void> {
      if (!this.newPlan || !this.newPlan.codename) return this.error('Something went wrong.')

      const { codename } = this.newPlan
      const { bundlePeriod } = this
      await this.setup({ codename, bundlePeriod, allowTrial: false })
    },

    async confirmPayment(): Promise<void> {
      await this.changePlan()
      this.$rModal.hide('confirmPayment')
      this.$rModal.show('upgradeSuccessful')
    },

    async confirmDowngrade(): Promise<void> {
      await this.changePlan()
      this.$rModal.hide('confirmDowngrade')
      this.$rModal.show('downgradeSuccessful')
    },
    handleContact(): void {
      openInNewTab('https://meetings-eu1.hubspot.com/michael-zyczkowski/talk-to-sales-renderro')
    },
    ...mapUserActions(['setup']),
    ...mapConfigActions(['getConfig']),
    ...mapNotificationsActions(['error']),
  },
})
